/*
#############################################################################
# Copyright (c) 2022-2023 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
#
# NVIDIA CORPORATION, its affiliates and licensors retain all intellectual
# property and proprietary rights in and to this material, related
# documentation and any modifications thereto. Any use, reproduction,
# disclosure or distribution of this material and related documentation
# without an express license agreement from NVIDIA CORPORATION or
# its affiliates is strictly prohibited.
###############################################################################
*/

/* eslint-disable no-console */
import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import { useDispatch, useSelector } from "react-redux";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import LunchDiningOutlinedIcon from "@mui/icons-material/LunchDiningOutlined";
import LocalCafeOutlinedIcon from "@mui/icons-material/LocalCafeOutlined";
import TapasOutlinedIcon from "@mui/icons-material/TapasOutlined";
import RiceBowlOutlinedIcon from "@mui/icons-material/RiceBowlOutlined";
import FastfoodOutlinedIcon from "@mui/icons-material/FastfoodOutlined";

import { useLocation, useNavigate } from "react-router-dom";
import { fetchItemCategories } from "./itemCategoriesSlice";
import { HTTP_STATUS } from "../../config/config";

const Item = styled(Paper)(({ theme }) => ({
  // backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
  background: "#2B2B2B",
  border: "1px solid #424242",
  boxShadow: "0px 8px 10px rgba(0, 0, 0, 0.4)",
}));

function Loading() {
  return (
    <Box xs={12}>
      <Skeleton height={40} animation='wave' />
    </Box>
  );
}

function getFoodIcon(item) {
  if (item === "drinks") {
    return <LocalCafeOutlinedIcon />;
  }
  if (item === "entrees") {
    return <LunchDiningOutlinedIcon />;
  }
  if (item === "salads") {
    return <RiceBowlOutlinedIcon />;
  }
  if (item === "sides") {
    return <TapasOutlinedIcon />;
  }
  return <FastfoodOutlinedIcon />;
}

function FoodCategories() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const categories = useSelector((state) => state.categories.data);
  const loading = useSelector((state) => state.categories.loading);
  const sessionActive = useSelector((state) => state.uuid.sessionActive);
  const location = useLocation().pathname;
  const selectedCategory = location.split("/").pop();
  useEffect(() => {
    dispatch(fetchItemCategories());
  }, []);
  const { items } = categories || {};
  const handleCategory = (e) => {
    console.log("handleCategory", e.currentTarget.value);
    navigate(`VARIETY_VIEW/${e.currentTarget.value}`);
  };
  const shouldRenderCategories =
    items != null &&
    sessionActive &&
    (items.includes(selectedCategory) ||
      location === "/WELCOME_VIEW" ||
      location === "/VARIETY_VIEW" ||
      location.includes("/FILTER_VIEW/category"));
  return (
    <Stack
      direction='row'
      sx={{
        display: shouldRenderCategories ? "inline" : "none",
        flexDirection: "row",
        flexGrow: 1,
        justifyContent: "center",
        width: "100%",
        marginBottom: 2.5
      }}
    >
      <Item
        sx={{
          "& button": {
            marginTop: 0,
            marginBottom: 0,
            marginRight: 1,
            marginLeft: 1,
          },
          p: 2,
          display: "block",
          overflow: "auto",
        }}
      >
        {loading === HTTP_STATUS.REJECTED && (
          <Alert severity='error'>Error — unable to load categories</Alert>
        )}
        {loading === HTTP_STATUS.PENDING && <Loading />}
        {loading === HTTP_STATUS.FULFILLED && (
          <ButtonGroup sx={{ m: 0, p: 0 }}>
            {items
              .filter((item) => {
                if (item === "ingredients" || item === "toppings") {
                  return false; // skip
                }
                return true;
              })
              .map((item) => (
                <Button
                  variant='contained'
                  sx={{
                    background: selectedCategory.includes(item)
                      ? "#76B900"
                      : "#616161",
                    boxShadow: "1px 2px 2px rgba(0, 0, 0, 0.15)",
                    borderRadius: "4px",
                  }}
                  key={item}
                  value={item}
                  onClick={handleCategory}
                  id={`${item}-category"`}
                  className='item-categories'
                  startIcon={getFoodIcon(item)}
                >
                  {item}
                </Button>
              ))}
          </ButtonGroup>
        )}
      </Item>
    </Stack>
  );
}

export default FoodCategories;
